import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export function useTownNursingHomes(countySlug: string | undefined, townSlug: string | undefined) {
  return useQuery({
    queryKey: ["nursing_homes_town", countySlug, townSlug],
    queryFn: async () => {
      if (!countySlug || !townSlug) return [];

      // Convert slug back to possible town name patterns
      // e.g. "dun-laoghaire" → "Dun Laoghaire"
      const townName = townSlug
        .split("-")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");

      const { data, error } = await supabase
        .from("nursing_homes")
        .select("*")
        .eq("county_slug", countySlug)
        .ilike("town", `%${townName}%`)
        .order("center_name", { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: !!countySlug && !!townSlug,
  });
}

/** Returns distinct towns with their nursing-home counts for a given county */
export function useTownList(countySlug: string | undefined) {
  return useQuery({
    queryKey: ["town_list", countySlug],
    queryFn: async () => {
      if (!countySlug) return [];

      const { data, error } = await supabase
        .from("nursing_homes")
        .select("town")
        .eq("county_slug", countySlug)
        .not("town", "is", null);

      if (error) throw error;

      // Aggregate counts client-side
      const counts: Record<string, number> = {};
      (data || []).forEach(({ town }) => {
        if (town) counts[town] = (counts[town] || 0) + 1;
      });

      return Object.entries(counts)
        .map(([name, count]) => ({
          name,
          count,
          slug: name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, ""),
        }))
        .sort((a, b) => b.count - a.count);
    },
    enabled: !!countySlug,
  });
}
